import type {
  AiCopyFilter,
  AiJobErrorCode,
  GroupEditAction,
  ProductLogo,
  AuditAction,
  CatalogIngestError,
  CatalogIngestMode,
  CatalogIngestStatus,
  CertificateFailure,
  CertificateIssueResult,
  DnsRecordType,
  DnsSetupKind,
  DomainReason,
  DomainStatus,
  ErrorCode,
  PlatformRole,
  ProductStatus,
  ShopperHiddenReason,
  ProvisioningWaitReason,
  ServiceCheck,
  ServiceTicketKind,
  ServiceTicketSeverity,
  ServiceTicketSource,
  ServiceTicketStatus,
  PublishBlocker,
  StoreHeroState,
  StoreTemplate,
  StoreTypeface,
  TenantKind,
  TenantStatus,
} from '@avrahami/contracts';
import type { BudgetState } from '../lib/ai.js';
import type { CopyField, CopyRowState } from '../lib/copy-types.js';

/** Server messages that deserve a precise translation (CONFLICT and friends). */
const serverMessages: Readonly<Record<string, string>> = {
  'The address must point at the platform before its certificate is issued':
    'אפשר להנפיק תעודה רק אחרי שהכתובת מפנה אל השרת והדומיין אומת',
  'The certificate of this address is being issued': 'התעודה של הכתובת הזו כבר בהנפקה',
  'Certificates are not issued from this screen here': 'בשרת הזה לא מנפיקים תעודות מהמסך',
  'This address is already taken': 'הכתובת כבר תפוסה',
  'The master tenant cannot be suspended': 'חנות האם אינה ניתנת להשעיה',
  'This person has already set a password and can sign in':
    'לאדם הזה כבר יש סיסמה, והוא יכול להיכנס',
  'This person is already on the platform staff': 'האדם הזה כבר בצוות הפלטפורמה',
  'The platform needs at least one owner': 'לפלטפורמה חייב להיות לפחות בעלים אחד',
  'You cannot change your own platform role': 'אי אפשר לשנות את התפקיד של עצמך',
  'You cannot remove yourself from the platform staff': 'אי אפשר להסיר את עצמך מהצוות',
  'Current password is incorrect': 'הסיסמה הנוכחית שגויה',
  'The code is not correct': 'הקוד שגוי',
  'This invitation link is invalid or has expired': 'קישור ההזמנה אינו תקף או שפג תוקפו',
  'Two-step verification is already on': 'אימות דו־שלבי כבר פעיל',
  'The opening banner of the store is already being written':
    'באנר הפתיחה של החנות כבר נכתב עכשיו. המתינו שהכתיבה תסתיים.',
};

/** The contracts' own validation messages (checked in the browser and by the API). */
const validation: Readonly<Record<string, string>> = {
  'duplicate product': 'מוצר מופיע פעמיים',
  'nothing to change': 'אין מה לשנות',
  'invalid hostname': 'הכתובת אינה תקינה. כתבו את שם הדומיין בלבד, למשל www.myshop.co.il',
  'this address belongs to the platform': 'זו כתובת של המערכת. כתבו את הדומיין של החנות',
  'this address cannot be reached from the internet': 'הכתובת הזו אינה נגישה מהאינטרנט',
  'enter a domain name, not an IP address': 'כתבו שם דומיין, לא כתובת IP',
  'invalid phone number': 'מספר טלפון אינו תקין',
  'an app password is 16 letters': 'סיסמת אפליקציה היא 16 אותיות באנגלית',
  REFUSED: 'Google לא קיבל את הכתובת והסיסמה. בדקו שהעתקתם את סיסמת האפליקציה במלואה.',
  'name a category or a manufacturer': 'בחרו קטגוריה, יצרן או את שניהם',
  empty: 'שדה חובה',
  'unknown category': 'הקטגוריה לא נמצאה בחנות. טענו את המסך מחדש.',
};

/** Field names in validation details (`/slug`, `/owner/email`, …). */
const fields: Readonly<Record<string, string>> = {
  slug: 'כתובת',
  displayName: 'שם',
  email: 'אימייל',
  password: 'סיסמה',
  newPassword: 'סיסמה חדשה',
};

/**
 * Every string the platform admin shows, in Hebrew. Components never hold literal text, so a
 * second language is a second dictionary (IMPLEMENTATION_PLAN Q12: Hebrew only for now).
 */
export const he = {
  app: { title: 'ניהול הפלטפורמה', product: 'אברהמי · פלטפורמת חנויות' },
  nav: {
    tenants: 'חנויות',
    audit: 'יומן פעולות',
    staff: 'צוות הפלטפורמה',
    domains: 'דומיינים',
    service: 'תקלות וקריאות',
    catalog: 'קטלוג',
    ai: 'תוכן AI ועלויות',
    account: 'החשבון שלי',
    logout: 'יציאה',
  },
  common: {
    save: 'שמירה',
    cancel: 'ביטול',
    confirm: 'אישור',
    close: 'סגירה',
    loading: 'טוען…',
    loadMore: 'טען עוד',
    retry: 'נסו שוב',
    copy: 'העתקה',
    copied: 'הועתק',
    none: '—',
    back: 'חזרה',
    yes: 'כן',
    no: 'לא',
    continue: 'המשך',
    invalidField: 'הערך אינו תקין',
    required: 'שדה חובה',
    taken: 'הערך כבר בשימוש',
    search: 'חיפוש',
    saved: 'נשמר',
    fixErrors: 'יש שדות שצריך לתקן',
    reload: 'טעינה מחדש',
    changedElsewhere:
      'מישהו אחר שינה את הנתונים האלה בינתיים. אפשר לטעון את הגרסה העדכנית (השינויים שלא נשמרו כאן יאבדו), או לשמור ולקבל הודעת התנגשות.',
    staleVersion: 'מישהו אחר שינה את הנתונים בינתיים. טענו את הגרסה העדכנית ונסו שוב.',
    noImage: 'אין תמונה',
  },
  login: {
    title: 'כניסה לניהול הפלטפורמה',
    email: 'אימייל',
    password: 'סיסמה',
    submit: 'כניסה',
    codeTitle: 'אימות דו־שלבי',
    codeHint: 'הזינו את הקוד בן 6 הספרות מאפליקציית האימות, או אחד מקודי השחזור.',
    code: 'קוד אימות',
    verify: 'אימות',
    startOver: 'כניסה עם משתמש אחר',
    invalid: 'אימייל או סיסמה שגויים',
    wrongCode: 'הקוד שגוי. נסו שוב.',
  },
  devCode: {
    label: 'סביבת פיתוח, הקוד הנוכחי:',
    use: 'הזנת הקוד',
  },
  mfaSetup: {
    title: 'הגדרת אימות דו־שלבי',
    intro:
      'צוות הפלטפורמה חייב אימות דו־שלבי. סרקו את הקוד באפליקציית אימות (Google Authenticator, Microsoft Authenticator, 1Password ועוד) והזינו את הקוד שהיא מציגה.',
    start: 'התחלת ההגדרה',
    manual: 'אי אפשר לסרוק? הזינו את המפתח ידנית:',
    code: 'הקוד מהאפליקציה',
    enable: 'הפעלה',
    recoveryTitle: 'קודי שחזור',
    recoveryIntro:
      'שמרו את הקודים האלה במקום בטוח. כל קוד משמש פעם אחת, כשאין גישה לאפליקציית האימות. הם לא יוצגו שוב.',
    recoveryDone: 'שמרתי את הקודים, המשך',
    qrAlt: 'קוד QR להגדרת אפליקציית האימות',
  },
  forgotPassword: {
    link: 'שכחתי סיסמה',
    title: 'איפוס סיסמה',
    hint: 'הזינו את האימייל שאיתו אתם נכנסים. נשלח אליו קישור לקביעת סיסמה חדשה.',
    email: 'אימייל',
    submit: 'שליחת קישור',
    sent: 'אם יש חשבון עם האימייל הזה, נשלח אליו קישור לקביעת סיסמה חדשה. הקישור תקף לחצי שעה. לא הגיע? בדקו גם בתיקיית הספאם.',
    notSetUp: 'שליחת מיילים עדיין לא הוגדרה במערכת. פנו למנהל הפלטפורמה.',
    goToLogin: 'חזרה למסך הכניסה',
  },
  passwordReset: {
    title: 'קביעת סיסמה חדשה',
    invalid: 'הקישור אינו תקף, כבר נוצל או שפג תוקפו. בקשו קישור חדש ב"שכחתי סיסמה".',
    missing: 'לא נמצא קישור. פתחו את הקישור המלא מהמייל.',
    forUser: 'סיסמה חדשה עבור',
    expires: 'הקישור בתוקף עד',
    password: 'סיסמה חדשה (לפחות 10 תווים)',
    confirm: 'אימות הסיסמה',
    mismatch: 'הסיסמאות אינן זהות',
    submit: 'שמירת הסיסמה',
    done: 'הסיסמה החדשה נשמרה, וכל החיבורים הקודמים לחשבון נותקו. אפשר להיכנס עכשיו.',
    goToLogin: 'למסך הכניסה',
  },
  invitation: {
    title: 'הגדרת סיסמה',
    loading: 'בודק את ההזמנה…',
    invalid: 'קישור ההזמנה אינו תקף או שפג תוקפו. בקשו קישור חדש ממי ששלח לכם אותו.',
    missing: 'לא נמצא קישור הזמנה. פתחו את הקישור המלא שקיבלתם.',
    forUser: 'הגדרת סיסמה עבור',
    expires: 'בתוקף עד',
    password: 'סיסמה חדשה (לפחות 10 תווים)',
    confirm: 'אימות הסיסמה',
    mismatch: 'הסיסמאות אינן זהות',
    submit: 'שמירת הסיסמה',
    done: 'הסיסמה נשמרה. אפשר להיכנס עכשיו.',
    goToLogin: 'למסך הכניסה',
  },
  noAccess: {
    title: 'אין הרשאה לניהול הפלטפורמה',
    body: 'החשבון מחובר, אבל אינו חלק מצוות הפלטפורמה. אם יש לכם חנות, היכנסו לממשק ניהול החנות.',
  },
  tenants: {
    title: 'חנויות',
    create: 'חנות חדשה',
    search: 'חיפוש לפי כתובת (slug)',
    searchSubmit: 'חיפוש',
    clear: 'ניקוי',
    empty: 'אין חנויות להצגה',
    name: 'שם',
    slug: 'כתובת',
    kind: 'סוג',
    status: 'מצב',
    created: 'נוצרה',
  },
  createTenant: {
    title: 'חנות חדשה',
    displayName: 'שם החנות',
    slug: 'כתובת (אותיות לטיניות קטנות, ספרות ומקפים)',
    slugHint: 'כתובת זמנית:',
    ownerName: 'שם בעל החנות',
    ownerEmail: 'אימייל בעל החנות',
    submit: 'יצירת החנות',
    created: 'החנות נוצרה',
    address: 'כתובת החנות',
    inviteTitle: 'קישור הזמנה לבעל החנות',
    inviteBody:
      'שלחו את הקישור לבעל החנות (עדיין אין שליחת אימייל). הקישור מאפשר להגדיר סיסמה, פעם אחת, והוא מוצג רק עכשיו.',
    existingOwner: 'לבעל החנות כבר יש חשבון עם סיסמה. הוא יראה את החנות החדשה בכניסה הבאה.',
    provisioning:
      'החנות בהקמה: הקטלוג מועתק אליה ברקע. בעל החנות יוכל להיכנס לניהול החנות כשההעתקה תסתיים. אפשר לעקוב אחרי ההתקדמות בעמוד החנות.',
    open: 'לעמוד החנות',
    manageWhenReady: 'הכפתור ייפתח כשהקמת החנות תסתיים.',
    manageFailed: 'הקמת החנות נכשלה. אפשר לנסות שוב בעמוד החנות.',
    exclusions: 'המוצרים שיוצגו בחנות',
    exclusionsHint:
      'החנות מקבלת את כל מוצרי הקטלוג, חוץ ממה שמסומן כאן "לא מוצג". מוצר שאינו מוצג לא יועתק לחנות, לא ייכתב לו תוכן שיווקי, והחנות לא תוכל למשוך אותו בהמשך. אפשר לשנות את הבחירה אחר כך בעמוד החנות.',
    exclusionsTooMany: 'יותר מדי מוצרים שאינם מוצגים: אפשר להסתיר עד {max} מוצרים בחנות.',
    exclusionsInvalid: 'חלק מהמוצרים שנבחרו כבר אינם בקטלוג. הסירו אותם מהבחירה ונסו שוב.',
    template: 'תבנית העיצוב של החנות',
    templateHint:
      'החנות נפתחת בצבעים ובגופן של התבנית. אפשר להחליף תבנית, צבעים וגופן בכל עת במסך "עיצוב החנות".',
    customerNumber: 'מספר הלקוח באתר המקורי',
    customerNumberHint:
      'כל חנות היא לקוח קיים של האתר המקורי. המחירים, מחיר העלות והמוצרים שהחנות מקבלת נלקחים ממה שנקבע ללקוח הזה באתר המקורי.',
  },
  provisioning: {
    title: 'הקמת החנות',
    none: 'החנות הזו לא עברה הקמה (חנות האם, או חנות שנוצרה לפני שלב ההקמה).',
    status: 'מצב',
    currentStep: 'שלב נוכחי',
    categories: 'קטגוריות שהועתקו',
    products: 'מוצרים שהועתקו',
    attempts: 'ניסיונות',
    startedAt: 'התחלה',
    finishedAt: 'סיום',
    stalled: 'אין התקדמות כבר זמן רב. ייתכן שהעובד ברקע (worker) אינו פועל. בדקו אותו ואז נסו שוב.',
    retry: 'ניסיון חוזר',
    retryBody: 'ההקמה תמשיך מהשלב שבו נעצרה. דבר שכבר הועתק לא יועתק שוב.',
    retried: 'ההקמה נשלחה לביצוע מחדש.',
    runStatus: {
      PENDING: 'ממתינה לביצוע',
      RUNNING: 'בביצוע',
      WAITING: 'ממתינה',
      FAILED: 'נכשלה',
      SUCCEEDED: 'הושלמה',
    },
    stepStatus: {
      PENDING: 'טרם החל',
      RUNNING: 'בביצוע',
      WAITING: 'ממתין',
      FAILED: 'נכשל',
      SUCCEEDED: 'הושלם',
    },
    steps: {
      CREATE_TENANT_RECORDS: 'יצירת החנות, הכתובת ובעל החנות',
      CLONE_CATEGORIES: 'העתקת הקטגוריות',
      CLONE_PRODUCTS: 'העתקת המוצרים והתמונות',
      OPEN_ADMIN: 'פתיחת ניהול החנות לבעלים',
      GENERATE_CONTENT: 'כתיבת תוכן שיווקי לכל מוצר',
      OPEN_STOREFRONT: 'פתיחת החנות לקונים',
    },
    errors: {
      UNEXPECTED: 'שגיאה בשלב הזה. הפרטים ביומן העובד ברקע (worker), לפי מזהה החנות.',
      STALLED: 'העובד ברקע נעצר שוב ושוב בשלב הזה, וההקמה הופסקה.',
    },
    waiting: {
      CONTENT_ENGINE_UNAVAILABLE:
        'מנוע התוכן אינו מוגדר בשרת, ולכן התוכן השיווקי לא נכתב. ניהול החנות פתוח לבעלים, אבל החנות תיפתח לקונים רק אחרי שלכל מוצר ייכתב תוכן שיווקי משלו.',
      CONTENT_IN_PROGRESS:
        'התוכן השיווקי של המוצרים נכתב עכשיו. ניהול החנות כבר פתוח לבעלים, והחנות תיפתח לקונים מעצמה כשהכתיבה תסתיים.',
    } satisfies Record<ProvisioningWaitReason, string>,
    copyProgress: 'תוכן שיווקי:',
    copyProgressMeter: 'התקדמות כתיבת התוכן השיווקי',
  },
  tenant: {
    back: 'לרשימת החנויות',
    summary: 'פרטים',
    customerNumber: 'מספר לקוח',
    customerNumberNone: 'לא נקבע',
    customerNumberEdit: 'החלפת לקוח',
    customerNumberSet: 'שיוך ללקוח',
    customerNumberLabel: 'מספר הלקוח באתר המקורי',
    customerNumberHint:
      'הלקוח נבדק מול האתר המקורי. המחירים ומחיר העלות של החנות יתעדכנו מיד לפי הלקוח החדש, וכך גם המק״טים (<מק״ט המקור>/<מספר הלקוח>). בקשות שכבר נשלחו שומרות את המק״ט שהיה בהן. מחירי המכירה שהחנות קבעה לא משתנים.',
    customerNumberSave: 'שמירה',
    customerNumberCancel: 'ביטול',
    customerNumberSaved: 'הלקוח נקרא מהאתר המקורי, והמחירים והמק״טים עודכנו.',
    customerNumberTaken: 'חנות אחרת כבר משויכת ללקוח הזה.',
    customerTitle: 'הלקוח באתר המקורי',
    customerIntro:
      'החנות היא לקוח של האתר המקורי. מחיר העלות של כל מוצר הוא המחיר שהלקוח הזה מקבל באתר המקורי (המחירון שלו, או מחיר מיוחד שנקבע לו), ומוצר שאין לו מחיר עבורו שם לא מקבל מחיר עלות. הדגם, היצרן ושאר הנתונים מוצגים בדיוק כמו באתר המקורי. הנתונים נקראים שוב מהאתר המקורי בכל לילה.',
    customerName: 'שם הלקוח',
    customerState: 'מצב באתר המקורי',
    customerActive: 'פעיל',
    customerInactive: 'מוקפא או לא פעיל',
    customerInactiveNote:
      'הלקוח מוקפא או לא פעיל באתר המקורי. החנות שומרת את המחירים שנקראו לאחרונה, עד שהלקוח יחזור להיות פעיל.',
    customerPriceList: 'מחירון',
    customerPrices: 'מוצרים עם מחיר ללקוח',
    customerPricesValue: '{count} מוצרים, מהם {special} במחיר מיוחד',
    customerCheckedAt: 'נקרא לאחרונה',
    customerRefresh: 'קריאה מחדש מהאתר המקורי',
    customerMissing:
      'החנות לא משויכת ללקוח של האתר המקורי (היא נפתחה לפני שהשיוך היה חובה), ולכן מחיר העלות שלה נלקח עדיין מהמחירון הכללי של הקטלוג. שייכו אותה ללקוח הקיים שלה.',
    customerErrors: {
      notFound: 'לא נמצא באתר המקורי לקוח עם המספר הזה. אפשר לשייך חנות רק ללקוח קיים.',
      inactive: 'הלקוח מוקפא או לא פעיל באתר המקורי, ולכן אי אפשר לשייך אליו חנות.',
      noPriceList:
        'ללקוח אין באתר המקורי מחירון משלו שאפשר להעביר לחנות. קבעו לו מחירון באתר המקורי ונסו שוב.',
      unavailable:
        'אין כרגע חיבור לאתר המקורי, ולכן אי אפשר לבדוק את הלקוח. נסו שוב בעוד כמה דקות.',
    },
    skuExample: 'מק״ט לדוגמה',
    domains: 'כתובות',
    owners: 'בעלי החנות',
    history: 'היסטוריית פעולות',
    primary: 'ראשית',
    passwordSet: 'הגדיר סיסמה',
    needsPassword: 'טרם הגדיר סיסמה',
    invitationUntil: 'הזמנה פתוחה עד',
    noInvitation: 'אין הזמנה פתוחה',
    newInvitation: 'קישור הזמנה חדש',
    newInvitationBody: 'הקישור הקודם הפסיק לעבוד. שלחו את החדש לבעל החנות.',
    setPassword: 'קביעת סיסמה חדשה',
    setPasswordHint:
      'הסיסמה של האימייל הזה תוחלף בכל החנויות שלו, וכל החיבורים הפתוחים שלו ינותקו. מסרו לו את הסיסמה החדשה.',
    newPassword: 'סיסמה חדשה (לפחות 10 תווים)',
    confirmPassword: 'אימות הסיסמה',
    passwordMismatch: 'הסיסמאות אינן זהות',
    savePassword: 'שמירת הסיסמה',
    cancelPassword: 'ביטול',
    passwordSaved: 'הסיסמה החדשה נשמרה.',
    suspend: 'השעיית החנות',
    reactivate: 'הפעלה מחדש',
    suspendTitle: 'להשעות את החנות?',
    suspendBody:
      'החנות תפסיק להיות זמינה לקונים, וצוות החנות יוכל לראות רק את מצבה. הנתונים לא נמחקים, ואפשר להפעיל אותה מחדש בכל עת.',
    reactivateTitle: 'להפעיל את החנות מחדש?',
    reactivateBody: 'החנות תחזור לפעול מיד, עם כל הנתונים שלה.',
    reason: 'סיבה (תירשם ביומן)',
    masterNote: 'חנות האם של אברהמי אינה ניתנת להשעיה.',
    manage: 'כניסה לניהול החנות',
    manageHint: 'ניהול החנות נפתח בלשונית חדשה, בדיוק כפי שבעל החנות רואה אותו.',
    manageBlocked: 'הדפדפן חסם את פתיחת הלשונית. אפשרו חלונות קופצים לאתר הזה ונסו שוב.',
    version: 'גרסה',
  },
  exclusions: {
    title: 'מוצרי הקטלוג בחנות',
    intro:
      'כל מוצרי הקטלוג, וליד כל מוצר אם הוא מוצג בחנות הזו. ברירת המחדל: מה שהאתר המקורי מציג ללקוח של החנות (מחירון, קטגוריות ומלאי). אפשר לבחור אחרת לכל מוצר, בשני הכיוונים. מוצר שאינו מוצג לא קיים מבחינת החנות: הוא לא מופיע לקונים וגם לא בניהול החנות.',
    countOne: 'מוצר אחד לא מוצג',
    countMany: '{count} מוצרים לא מוצגים',
    empty: 'כל מוצרי הקטלוג מוצגים בחנות.',
    reasonSite: 'לפי האתר',
    reasonSiteHelp: 'האתר המקורי לא מציג את המוצר ללקוח של החנות (ברירת המחדל)',
    reasonPlatform: 'הוסתר בבחירה',
    reasonPlatformHelp: 'הוסתר בבחירה, גם אם האתר המקורי מציג אותו ללקוח',
    shownByChoice: 'מוצג בבחירה',
    shownByChoiceHelp: 'מוצג בבחירה, גם אם האתר המקורי לא מציג אותו ללקוח',
    viewForbidden: 'אין לך הרשאה לראות אילו מוצרים אינם מוצגים בחנות',
    showTitleOne: 'להציג את המוצר בחנות?',
    showTitleMany: 'להציג {count} מוצרים בחנות?',
    showBody:
      'המוצר יופיע בחנות, לקונים ובניהול החנות, גם אם האתר המקורי לא מציג אותו ללקוח. מוצר שעוד לא היה בחנות יתווסף אליה עכשיו. הצגה של מוצר שהאתר כבר מציג מחזירה אותו לברירת המחדל.',
    hideTitleOne: 'להפסיק להציג את המוצר בחנות?',
    hideTitleMany: 'להפסיק להציג {count} מוצרים בחנות?',
    hideBody:
      'המוצר ייעלם מהחנות: לא יוצג לקונים ולא יופיע בניהול החנות, כאילו אינו קיים. הוא לא עובר לארכיון, וכשיוצג שוב יחזור כפי שהיה. הסתרה של מוצר שהאתר לא מציג ממילא מחזירה אותו לברירת המחדל.',
    addedOne: 'מוצר אחד אינו מוצג עוד בחנות.',
    addedMany: '{count} מוצרים אינם מוצגים עוד בחנות.',
    liftedOne: 'מוצר אחד מוצג עכשיו בחנות.',
    liftedMany: '{count} מוצרים מוצגים עכשיו בחנות.',
    copiedOne: 'מוצר אחד שלא היה בחנות מתווסף אליה עכשיו.',
    copiedMany: '{count} מוצרים שלא היו בחנות מתווספים אליה עכשיו.',
    unchanged: 'לא היה מה לשנות: הרשימה כבר מעודכנת.',
    invalid: 'חלק מהמוצרים שנבחרו כבר אינם בקטלוג. בחרו שוב.',
    tooMany: 'יותר מדי מוצרים שאינם מוצגים. אפשר להסתיר עד {max} מוצרים בחנות.',
    notCustomer: 'רק בחנות לקוח אפשר לבחור אילו מוצרים מוצגים.',
    table: {
      label: 'מוצרי הקטלוג',
      search: 'חיפוש (שם, מק״ט או דגם)',
      view: 'סינון',
      viewAll: 'כל המוצרים',
      viewShown: 'רק מה שמוצג',
      viewHidden: 'רק מה שלא מוצג',
      empty: 'לא נמצאו מוצרים מתאימים',
      name: 'שם',
      sku: 'מק״ט',
      model: 'דגם',
      status: 'בחנות',
      actions: 'פעולות',
      shown: 'מוצג',
      hidden: 'לא מוצג',
      show: 'הצגה',
      hide: 'הסתרה',
      showSelected: 'הצגת הנבחרים ({count})',
      hideSelected: 'הסתרת הנבחרים ({count})',
      selectAll: 'בחירת כל המוצרים ברשימה',
      selectView: 'בחירת הכל ({count})',
      selectCategory: 'בחירת כל המוצרים בקטגוריה',
      select: 'בחירת המוצר',
      chosenOne: 'מוצר אחד נבחר',
      chosenMany: '{count} מוצרים נבחרו',
      noneChosen: 'לא נבחרו מוצרים',
      clear: 'ניקוי הבחירה',
    },
  },
  aiCopy: {
    title: 'תוכן שיווקי',
    link: 'ניהול התוכן השיווקי',
    linkFor: 'ניהול התוכן השיווקי של',
    back: 'לעמוד החנות',
    forbidden: 'אין לך הרשאה לנהל את התוכן השיווקי של חנות',
    notFound: 'החנות לא נמצאה. ייתכן שהיא הוסרה; חזרו לרשימת החנויות.',
    summary: 'מצב התוכן',
    intro:
      'הטקסט השיווקי של מוצרי הקטלוג נכתב אוטומטית במיוחד לחנות הזו: כותרת, תיאור וטקסטים לגוגל. הפרטים הטכניים של המוצרים אינם משתנים. מוצר בלי תוכן שיווקי מוסתר מהקונים. התוכן מנוהל כאן, על ידי צוות הפלטפורמה; בעל החנות יכול רק לערוך ידנית את הטקסטים של מוצר בעמוד המוצר שלו.',
    progress: '{ready} מתוך {total} מוכנים',
    progressLabel: 'התקדמות כתיבת התוכן',
    working: 'התוכן נכתב עכשיו. המסך מתעדכן מעצמו.',
    paused:
      'הכתיבה מושהית: המכסה החודשית לתוכן AI נוצלה (של החנות, או של הפלטפורמה כולה). הכתיבה תמשיך בתחילת החודש הבא, או כשהמכסה תוגדל.',
    stopped: 'הכתיבה נעצרה. התוכן שממתין נשאר בתור, והכתיבה תמשיך מאותה נקודה כשתמשיכו אותה.',
    stop: 'עצירת הכתיבה',
    resume: 'המשך הכתיבה',
    stoppedNow: 'הכתיבה נעצרה. מה שנכתב ברגע זה יסתיים, והשאר ממתין בתור.',
    resumedNow: 'הכתיבה ממשיכה.',
    flyers: {
      show: 'פלייר',
      hide: 'הסתרת הפלייר',
      download: 'הורדת הפלייר (PNG)',
      drawing: 'מכין את הפלייר…',
      imageLabel: 'פלייר שיווקי: {title}',
      downloadFailed: 'לא הצלחנו לשמור את התמונה. נסו שוב.',
      noPrice: 'המחיר מופיע בפלייר רק כשהחנות מציגה מחירים ולמוצר יש מחיר.',
    },
    storeSeo: {
      title: 'קידום בגוגל של החנות',
      intro: 'הכותרת והתיאור של דף הבית בתוצאות החיפוש. בעל החנות יכול לערוך אותם בהגדרות החנות.',
      seoTitle: 'כותרת דף הבית',
      seoDescription: 'תיאור דף הבית',
      writing: 'בכתיבה',
      queued: 'הטקסטים נכנסו לתור הכתיבה.',
      regenerateTitle: 'לכתוב מחדש את טקסטי הקידום של החנות?',
      regenerateBody:
        'הכותרת והתיאור הנוכחיים של דף הבית יוחלפו בגרסה חדשה, גם אם בעל החנות כתב אותם, ואי אפשר יהיה לשחזר אותם.',
    },
    approvalSwitch: 'לאשר תוכן לפני פרסום',
    approvalHint:
      'פעיל: תוכן חדש מוצג לקונים רק אחרי שאושר במסך הזה. כבוי: תוכן חדש מתפרסם מיד, ואפשר לערוך אותו בכל עת.',
    approvalOn: 'מעכשיו תוכן חדש ימתין לאישור במסך הזה לפני הפרסום.',
    approvalOff: 'מעכשיו תוכן חדש מתפרסם מיד.',
    filters: 'סינון לפי מצב התוכן',
    filter: {
      GENERATED: 'נכתב אוטומטית',
      IN_PROGRESS: 'בכתיבה',
      FAILED: 'נכשל',
      WITHOUT_COPY: 'בלי תוכן',
      READY: 'מוכן',
    } satisfies Record<AiCopyFilter, string>,
    rowState: {
      GENERATED: 'נכתב אוטומטית',
      IN_PROGRESS: 'בכתיבה',
      FAILED: 'נכשל',
      WITHOUT_COPY: 'בלי תוכן',
      APPROVED: 'אושר',
      EDITED: 'נערך ידנית',
    } satisfies Record<CopyRowState, string>,
    empty: {
      GENERATED: 'אין תוכן אוטומטי שעוד לא אושר או נערך',
      IN_PROGRESS: 'שום תוכן אינו נכתב כרגע',
      FAILED: 'אין מוצרים שכתיבת התוכן שלהם נכשלה',
      WITHOUT_COPY: 'אין מוצרים בלי תוכן (חוץ מאלה שבכתיבה או שנכשלו)',
      READY: 'עדיין אין תוכן שאושר או נערך',
    } satisfies Record<AiCopyFilter, string>,
    noResults: 'לא נמצאו מוצרים מתאימים',
    list: 'המוצרים',
    search: 'חיפוש (שם, כותרת, מק״ט או דגם)',
    sku: 'מק״ט',
    select: 'בחירת המוצר',
    selectAll: 'בחירת כל המוצרים שברשימה',
    clearSelection: 'ניקוי הבחירה',
    fields: {
      title: 'כותרת בחנות',
      description: 'תיאור',
      seoTitle: 'כותרת לגוגל (SEO)',
      seoDescription: 'תיאור לגוגל (SEO)',
    } satisfies Record<CopyField, string>,
    noText: 'עדיין אין טקסט',
    showMore: 'הצג עוד',
    showLess: 'הצג פחות',
    blockers: 'המוצר עדיין לא יכול להיות מוצג בחנות:',
    jobFailed: 'הכתיבה נכשלה: {reason}. אפשר לנסות שוב.',
    jobErrorUnknown: 'הסיבה אינה ידועה',
    jobErrors: {
      FACT_GUARD: 'בטקסט שנכתב הופיע מספר או דגם שאינו בפרטי המוצר, ולכן הוא נפסל',
      LANGUAGE: 'בטקסט שנכתב הופיעו אותיות בשפה זרה, ולכן הוא נפסל',
      TOO_SIMILAR: 'הטקסט שנכתב היה דומה מדי לטקסט המקורי, ולכן הוא נפסל',
      INVALID_OUTPUT: 'לא התקבל טקסט שאפשר להשתמש בו',
      PROVIDER_UNAVAILABLE: 'שירות הכתיבה לא היה זמין',
      STALLED: 'הכתיבה נתקעה שוב ושוב',
      UNEXPECTED: 'אירעה תקלה לא צפויה',
    } satisfies Record<AiJobErrorCode, string>,
    approve: 'אישור',
    edit: 'עריכה',
    regenerate: 'כתיבה מחדש',
    approveSelected: 'אישור המסומנים ({count})',
    approveAll: 'אישור הכול ({count})',
    retryFailed: 'ניסיון נוסף לכל הנכשלים',
    generateMissing: 'כתיבת תוכן למוצרים שחסר להם',
    regenerateAll: 'כתיבה מחדש של כל התוכן שנכתב אוטומטית',
    overLimit: 'אפשר לאשר עד {max} מוצרים מסומנים בכל פעם. לאישור של הכול השתמשו ב"אישור הכול".',
    approveAllTitle: 'לאשר את כל התוכן שנכתב אוטומטית?',
    approveAllOne: 'התוכן של מוצר אחד יאושר. מוצר שרק האישור חסר לו יוצג לקונים.',
    approveAllMany: 'התוכן של {count} מוצרים יאושר. מוצרים שרק האישור חסר להם יוצגו לקונים.',
    regenerateTitle: 'לכתוב את התוכן מחדש?',
    regenerateBody: 'הטקסט הנוכחי של "{name}" יוחלף בגרסה חדשה, ואי אפשר יהיה לשחזר אותו.',
    regenerateAllTitle: 'לכתוב מחדש את כל התוכן שנכתב אוטומטית?',
    regenerateAllOne:
      'מוצר אחד ייכתב מחדש: התוכן שנכתב לו אוטומטית, ועוד לא אושר או נערך, יוחלף בגרסה חדשה ואי אפשר יהיה לשחזר אותו.',
    regenerateAllMany:
      '{count} מוצרים ייכתבו מחדש: התוכן שנכתב להם אוטומטית, ועוד לא אושר או נערך, יוחלף בגרסה חדשה ואי אפשר יהיה לשחזר אותו.',
    regenerateAllCost:
      'כל כתיבה מחדש היא קריאה בתשלום לספק ה-AI, והיא נזקפת למכסה החודשית של החנות.',
    approvedNone: 'לא נמצא תוכן אוטומטי לאישור.',
    approvedOne: 'התוכן של מוצר אחד אושר.',
    approvedMany: 'התוכן של {count} מוצרים אושר.',
    publishedOne: 'מוצר אחד נוסף מוצג עכשיו בחנות.',
    publishedMany: '{count} מוצרים נוספים מוצגים עכשיו בחנות.',
    queuedNone: 'אין מה להוסיף לתור: התוכן כבר בכתיבה או שאין מוצרים מתאימים.',
    queuedOne: 'מוצר אחד נכנס לתור הכתיבה.',
    queuedMany: '{count} מוצרים נכנסו לתור הכתיבה.',
    alreadyWriting: 'התוכן כבר בכתיבה.',
    selectionChanged: 'חלק מהמוצרים שסומנו כבר אינם ממתינים לאישור. הרשימה עודכנה; סמנו שוב.',
    counter: '{count} מתוך {max} תווים',
    openPage: 'לדף המוצר',
  },
  groupEdit: {
    title: 'עריכה לקבוצה שלמה',
    intro:
      'עריכה אחת של הטקסט בכל המוצרים של קטגוריה (כולל תתי־הקטגוריות שלה), של יצרן, או של שניהם יחד. הטקסט הקיים נערך: מחליפים בו מילים או מוסיפים לו טקסט. שום דבר לא נכתב מחדש. התוצאה נשמרת כעריכה ידנית, ולכן העדכון היומי של הקטלוג לא ידרוס אותה.',
    category: 'קטגוריה',
    anyCategory: 'כל הקטגוריות',
    uncategorized: 'בלי קטגוריה',
    manufacturer: 'יצרן',
    anyManufacturer: 'כל היצרנים',
    field: 'שדה',
    action: 'פעולה',
    actions: {
      REPLACE: 'החלפת טקסט',
      PREPEND: 'הוספה בתחילת הטקסט',
      APPEND: 'הוספה בסוף הטקסט',
    } satisfies Record<GroupEditAction, string>,
    find: 'הטקסט להחלפה',
    findHint: 'בדיוק כפי שהוא כתוב, כולל רווחים',
    replace: 'הטקסט החדש',
    replaceHint: 'ריק = מחיקת הטקסט',
    text: 'הטקסט להוספה',
    textHint: 'בכותרות מתווסף רווח ביניהם, ובתיאור שורה ריקה. מוצר שהטקסט כבר מופיע בו לא ישתנה.',
    needGroup: 'בחרו קטגוריה, יצרן או את שניהם.',
    previewButton: 'בדיקה לפני שמירה',
    apply: 'שמירה לכל הקבוצה',
    confirmTitle: 'לשמור את העריכה בכל הקבוצה?',
    confirmBody:
      'הטקסט ישתנה ב־{count} מוצרים. אין ביטול אוטומטי: כדי לחזור צריך עריכה הפוכה (או עריכה של כל מוצר בנפרד).',
    examples: 'דוגמאות',
    before: 'לפני',
    after: 'אחרי',
    inGroup: 'בקבוצה {count} מוצרים עם תוכן.',
    emptyGroup: 'אין בקבוצה הזו מוצרים עם תוכן.',
    skipped: '{count} יישארו כמו שהם: הטקסט היה יוצא ארוך מדי לשדה, או ריק.',
    published: '{count} מוצרים שחיכו לאישור מוצגים עכשיו בחנות.',
    preview: {
      none: 'העריכה לא תשנה אף מוצר.',
      changedOne: 'הטקסט ישתנה במוצר אחד.',
      changedMany: 'הטקסט ישתנה ב־{count} מוצרים.',
    },
    done: {
      none: 'אף מוצר לא השתנה.',
      changedOne: 'הטקסט השתנה במוצר אחד.',
      changedMany: 'הטקסט השתנה ב־{count} מוצרים.',
    },
    changed: 'הטקסט בקבוצה השתנה בינתיים. בדקו שוב לפני השמירה.',
  },
  productPage: {
    back: 'לתוכן השיווקי',
    forbidden: 'אין לך הרשאה לנהל את דפי המוצרים של חנות',
    notFound:
      'המוצר לא נמצא בחנות. ייתכן שהוא הוסר מהחנות, או שזה מוצר של החנות עצמה (את אלה מנהלים בניהול החנות).',
    intro:
      'דף המוצר בחנות, כפי שבעל החנות רואה אותו בניהול שלו. אפשר לערוך את הטקסט שנכתב, את היתרונות והרשימות, את מצב המוצר, את התמונות והלוגו. כאן אין כתיבה מחדש של התוכן או של הפלייר.',
    contentState: 'מצב התוכן',
    approve: 'אישור התוכן',
    approved: 'התוכן אושר.',
    blockers: 'המוצר עדיין לא יכול להיות מוצג בחנות:',
    refused: 'אי אפשר להציג את המוצר בחנות: {reasons}.',
    status: 'מצב המוצר',
    statusHint: 'כדי להסיר מוצר מהחנות, הוציאו אותו מהחנות בעמוד החנות (מוצרים שאינם מוצגים).',
    content: 'התוכן השיווקי',
    /** The strengths, "ideal for" lines and care instructions (adr/0055). */
    lists: {
      title: 'יתרונות, מתאים במיוחד ל, תחזוקה וניקיון',
      note: 'רשימה שנערכה ידנית (כאן או בניהול החנות) נשארת כפי שהיא: עדכוני הקטלוג לא דורסים אותה. רשימה שלא נערכה מתעדכנת אוטומטית מהקטלוג.',
      names: {
        features: 'יתרונות',
        idealFor: 'מתאים במיוחד ל...',
        care: 'הוראות תחזוקה וניקיון',
      },
      states: {
        EDITED: 'נערך ידנית',
        AUTO: 'נכתב אוטומטית',
        PENDING: 'ייכתב אוטומטית',
        WRITING: 'נכתב עכשיו מהקטלוג…',
      },
      empty: 'אין שורות. רשימה ריקה לא מוצגת בעמוד המוצר.',
      limit: 'עד {count} שורות',
      featureIcon: 'סמל',
      featureTitle: 'כותרת',
      featureText: 'משפט הסבר',
      idealForLine: 'סוג עסק',
      careLine: 'הוראה',
      addLine: 'הוספת שורה',
      removeLine: 'הסרה',
      icons: {
        SPEED: 'מהירות',
        CLEAN: 'ניקיון והיגיינה',
        SHIELD: 'עמידות ובטיחות',
        SAVINGS: 'חיסכון',
        QUALITY: 'איכות',
        CAPACITY: 'קיבולת',
        VARIETY: 'מגוון',
        POWER: 'עוצמה',
        COLD: 'קירור',
        HEAT: 'חימום ובישול',
        PRECISION: 'דיוק ושליטה',
        EASY: 'קלות הפעלה',
        SPACE: 'חיסכון במקום',
        SERVICE: 'שירות ותחזוקה',
      },
    },
    restoreList: {
      label: 'כתיבה מחדש מהקטלוג',
      hint: 'הנוסח הידני יוחלף בנוסח שייכתב אוטומטית מהקטלוג (קריאה ל-AI מתקציב החנות), ויתעדכן ממנו מעכשיו.',
      unsaved: 'שמרו קודם את השינויים בדף',
    },
    restoreConfirm: 'לכתוב מחדש את "{list}" מהקטלוג? הנוסח הידני יוחלף.',
    facts: 'פרטי המוצר',
    factsLocked: 'הפרטים מגיעים מהקטלוג ומתעדכנים איתו, ולכן אינם נערכים.',
    factName: 'שם',
    factSku: 'מק״ט',
    barcode: 'ברקוד',
    model: 'דגם',
    manufacturer: 'יצרן',
    extraSpecs: 'פרטים נוספים של החנות',
    specLabel: 'כותרת',
    specValue: 'ערך',
    addSpec: 'הוספת שורה',
    removeSpec: 'הסרה',
    saved: 'דף המוצר נשמר.',
    gallery: 'תמונות',
    galleryEmpty: 'אין תמונות למוצר.',
    galleryFull: 'הגעתם למספר התמונות המרבי.',
    image: 'תמונה',
    altText: 'תיאור תמונה',
    moveUp: 'הזזה למעלה',
    moveDown: 'הזזה למטה',
    remove: 'הסרה',
    addImage: 'הוספת תמונה',
    saveGallery: 'שמירת התמונות',
    gallerySaved: 'התמונות נשמרו.',
    logo: {
      title: 'לוגו היצרן',
      hint: 'הלוגו שמוצג על כרטיס המוצר ובדף המוצר בחנות.',
      alt: 'לוגו היצרן',
      none: 'אין לוגו',
      catalogNone: 'לקטלוג אין לוגו ליצרן הזה.',
      upload: 'העלאת לוגו',
      replace: 'החלפת הלוגו',
      remove: 'בלי לוגו',
      backToCatalog: 'חזרה ללוגו של הקטלוג',
      sources: {
        CATALOG: 'הלוגו של הקטלוג, כמו באתר המקורי.',
        UPLOAD: 'לוגו שהועלה לחנות במקום הלוגו של הקטלוג.',
        NONE: 'החנות לא מציגה לוגו למוצר הזה.',
      } satisfies Record<ProductLogo['source'], string>,
    },
    flyer: 'פלייר שיווקי',
    flyerIntro:
      'הפלייר שבעל החנות מוריד מדף המוצר: הפלייר של התבנית של החנות, בצבעים ובלוגו שלה, עם הכותרת, התיאור, המפרט והמחיר של המוצר בחנות. שינוי בתבנית, בצבעים או בטקסט משנה אותו מיד.',
  },
  upload: {
    drop: 'גררו תמונה לכאן',
    dropMany: 'גררו תמונות לכאן',
    or: 'או',
    chooseFile: 'בחירת קובץ',
    uploading: 'מעלה…',
    close: 'סגירה',
    formats: 'JPEG, PNG, GIF, WebP, AVIF או SVG, עד 5MB',
    tooLarge: 'הקובץ גדול מ־5MB',
    notImage: 'הקובץ אינו תמונה',
    badSvg: 'לא הצלחנו לקרוא את קובץ ה־SVG. אפשר לייצא אותו שוב או להעלות PNG',
    tooMany: { one: 'אפשר להוסיף תמונה אחת', many: 'אפשר להוסיף עד {count} תמונות' },
  },
  productStatus: {
    DRAFT: 'טיוטה',
    ACTIVE: 'מוצג בחנות',
    HIDDEN: 'מוסתר',
    ARCHIVED: 'בארכיון',
  } satisfies Record<ProductStatus, string>,
  /** Why shoppers do not see an ACTIVE product (adr/0046). */
  shopperHidden: {
    NOT_ON_SITE: 'לא מוצג: לא מוצג ללקוח באתר המקורי',
    CATEGORY_HIDDEN: 'לא מוצג: הקטגוריה מוסתרת',
  } satisfies Record<ShopperHiddenReason, string>,
  publishBlockers: {
    ARCHIVED: 'המוצר בארכיון',
    NO_COPY: 'אין תוכן שיווקי',
  } satisfies Record<PublishBlocker, string>,
  audit: {
    title: 'יומן פעולות',
    allTenants: 'כל החנויות והפלטפורמה',
    tenant: 'חנות',
    when: 'מתי',
    action: 'פעולה',
    actor: 'מבצע',
    resource: 'על מה',
    details: 'פרטים',
    platform: 'פלטפורמה',
    empty: 'אין אירועים להצגה',
    system: 'מערכת',
    anonymous: 'לא מזוהה',
  },
  staff: {
    title: 'צוות הפלטפורמה',
    invite: 'הוספת איש צוות',
    email: 'אימייל',
    name: 'שם',
    role: 'תפקיד',
    submit: 'הוספה',
    you: '(את/ה)',
    mfa: 'אימות דו־שלבי',
    mfaOn: 'פעיל',
    mfaOff: 'לא הוגדר',
    status: 'חשבון',
    remove: 'הסרה',
    removeTitle: 'להסיר מצוות הפלטפורמה?',
    removeBody: 'ההרשאות לפלטפורמה יוסרו מיד. החשבון עצמו, וחנויות שהוא חבר בהן, לא יושפעו.',
    inviteTitle: 'קישור הזמנה',
    inviteBody: 'שלחו את הקישור לאיש הצוות. הוא מוצג רק עכשיו ותקף לשבוע.',
    existingAccount: 'לאדם הזה כבר יש חשבון. התפקיד נוסף, והוא יראה אותו בכניסה הבאה.',
    newInvitation: 'קישור חדש',
  },
  account: {
    title: 'החשבון שלי',
    password: 'שינוי סיסמה',
    current: 'הסיסמה הנוכחית',
    next: 'סיסמה חדשה (לפחות 10 תווים)',
    confirm: 'אימות הסיסמה החדשה',
    changed: 'הסיסמה שונתה. כל ההתחברויות האחרות נותקו.',
    mfa: 'אימות דו־שלבי',
    mfaOn: 'פעיל',
    mfaOff: 'לא הוגדר',
    setup: 'הגדרה',
    remaining: 'קודי שחזור שנותרו:',
    regenerate: 'יצירת קודי שחזור חדשים',
    regenerateHint: 'הזינו קוד מהאפליקציה. הקודים הקודמים יפסיקו לעבוד.',
  },
  catalog: {
    title: 'קטלוג אברהמי',
    intro:
      'הקטלוג הראשי נמשך מהמערכת של אברהמי. בכל לילה נמשכים רק המוצרים שהשתנו מאז העדכון הקודם ("שינויים"). פעם בשבוע עוברים על כל הקטלוג ("מלא"), כדי לוודא ששום שינוי לא התפספס ולסמן מוצרים שכבר אינם מופיעים אצל אברהמי. חנויות הלקוחות מקבלות עותק של הקטלוג; שינוי בחנות לעולם אינו משנה את הקטלוג הראשי.',
    summary: 'מה יש בקטלוג',
    activeProducts: 'מוצרים פעילים',
    discontinued: 'הופסקו במקור',
    withoutImage: 'בלי תמונה (יוסתרו בחנויות)',
    withoutPrice: 'בלי מחיר במקור (לא יוצגו בחנויות עד שהחנות תקבע מחיר)',
    bundles: 'מוצרי סט',
    categories: 'קטגוריות',
    mediaFiles: 'קובצי תמונה',
    lastSuccess: 'עדכון מוצלח אחרון',
    never: 'עדיין לא',
    runNow: 'עדכון עכשיו',
    runFull: 'סריקה מלאה',
    fullTitle: 'להריץ סריקה מלאה?',
    fullBody:
      'הסריקה עוברת על כל הקטלוג. מוצר שכבר אינו מופיע אצל אברהמי יסומן "הופסק במקור" (הוא לא יימחק, וחנויות הלקוחות יחליטו מה לעשות בו).',
    queued: 'העדכון נכנס לתור ויתחיל בדקות הקרובות.',
    alreadyQueued: 'עדכון כבר ממתין בתור; הבקשה לא נוספה שוב.',
    runs: 'היסטוריית עדכונים',
    noRuns: 'עוד לא היה עדכון',
    started: 'התחיל',
    finished: 'הסתיים',
    mode: 'סוג',
    status: 'מצב',
    products: 'מוצרים (חדש / עודכן / הופסק)',
    rejected: 'נדחו',
    images: 'תמונות (הועתקו / נכשלו)',
    error: 'שגיאה',
    modes: {
      FULL: 'מלא',
      INCREMENTAL: 'שינויים',
    } satisfies Record<CatalogIngestMode, string>,
    statuses: {
      RUNNING: 'רץ',
      SUCCEEDED: 'הצליח',
      FAILED: 'נכשל',
    } satisfies Record<CatalogIngestStatus, string>,
    errors: {
      FEED_UNREACHABLE: 'אין חיבור לקטלוג של אברהמי',
      FEED_REJECTED: 'הקטלוג של אברהמי סירב לבקשה (מפתח שגוי?)',
      FEED_INVALID: 'הקטלוג שהתקבל אינו תקין; שום דבר לא סומן כהופסק',
      ABANDONED: 'העדכון נקטע (תהליך הרקע הופעל מחדש)',
      INTERNAL: 'תקלה פנימית',
    } satisfies Record<CatalogIngestError, string>,
  },
  ai: {
    title: 'תוכן AI ועלויות',
    intro:
      'מנוע התוכן כותב לכל חנות תוכן שיווקי משלה, וכל קריאה לספק ה-AI נרשמת כאן. לפלטפורמה יש מכסה חודשית כוללת, ולכל חנות מכסה חודשית משלה. כשמכסה מנוצלת, הכתיבה מושהית עד תחילת החודש הבא או עד שהמכסה תוגדל.',
    refresh: 'רענון',
    summary: 'החודש בפלטפורמה',
    month: 'חודש',
    spent: 'הוצאה החודש',
    platformLimit: 'מכסה חודשית לפלטפורמה',
    defaultTenantLimit: 'מכסת ברירת מחדל לחנות',
    platformMeter: 'ניצול המכסה החודשית של הפלטפורמה',
    tenants: 'חנויות',
    empty: 'אף חנות עוד לא השתמשה במנוע התוכן החודש',
    store: 'חנות',
    limit: 'מכסה',
    usage: 'ניצול',
    usageOf: 'ניצול המכסה של',
    calls: 'קריאות לספק',
    tokens: 'טוקנים (קלט / פלט)',
    jobs: 'משימות (בתור / בביצוע / הצליחו / נכשלו)',
    limitDefault: 'ברירת מחדל',
    limitCustom: 'מותאם',
    of: 'מתוך',
    platformState: {
      OK: 'ההוצאה בתוך המכסה החודשית של הפלטפורמה.',
      NEAR: 'רוב המכסה החודשית של הפלטפורמה כבר נוצל. כשהיא תנוצל כולה, הכתיבה תושהה בכל החנויות.',
      REACHED:
        'המכסה החודשית של הפלטפורמה נוצלה. כתיבת התוכן מושהית בכל החנויות עד תחילת החודש הבא או עד שהמכסה תוגדל.',
    } satisfies Record<BudgetState, string>,
    tenantState: {
      OK: 'ההוצאה בתוך המכסה החודשית של החנות.',
      NEAR: 'רוב המכסה החודשית של החנות כבר נוצל. כשהיא תנוצל כולה, הכתיבה תושהה.',
      REACHED:
        'המכסה החודשית של החנות נוצלה. כתיבת התוכן מושהית עד תחילת החודש הבא או עד שהמכסה תוגדל.',
    } satisfies Record<BudgetState, string>,
    stateBadge: {
      OK: 'בתוך המכסה',
      NEAR: 'קרוב למכסה',
      REACHED: 'המכסה נוצלה',
    } satisfies Record<BudgetState, string>,
    viewForbidden: 'אין לך הרשאה לראות את נתוני התוכן והעלויות',
    card: {
      title: 'תוכן שיווקי (AI)',
      products: 'מוצרים עם תוכן שיווקי משלהם',
      productsMeter: 'מוצרים עם תוכן שיווקי',
      ready: 'מוכנים',
      jobsQueued: 'משימות בתור',
      jobsRunning: 'משימות בביצוע',
      jobsSucceeded: 'משימות שהצליחו',
      jobsFailed: 'משימות שנכשלו',
      budget: 'הוצאה החודש',
      budgetMeter: 'ניצול המכסה החודשית של החנות',
      limitField: 'מכסה חודשית לחנות (בדולרים)',
      limitHint:
        'בין 0 ל-100,000 דולר, עד שתי ספרות אחרי הנקודה. ברירת המחדל של הפלטפורמה חלה כשאין לחנות מכסה משלה.',
      limitInvalid: 'הזינו סכום בדולרים בין 0 ל-100,000, עם שתי ספרות אחרי הנקודה לכל היותר',
      reset: 'חזרה לברירת המחדל',
      saved: 'המכסה החודשית של החנות נשמרה.',
      resetDone: 'החנות חזרה למכסת ברירת המחדל של הפלטפורמה.',
      manageForbidden: 'אין לך הרשאה לשנות את מכסת התוכן של חנות',
      notFound: 'החנות לא נמצאה. ייתכן שהיא הוסרה; חזרו לרשימת החנויות.',
    },
  },
  domains: {
    title: 'דומיינים',
    intro:
      'הדומיינים שחוברו לחנויות. דומיין מחברים לחנות ממסך "ניהול דומיין" שלה (לחיצה על שם החנות), והמערכת בודקת את ה-DNS ואת תעודת האבטחה ברקע. כאן רואים מה ממתין, מה נכשל ומה התנתק.',
    refresh: 'רענון',
    viewForbidden: 'אין לך הרשאה לראות את הדומיינים של החנויות',
    summary: 'לפי מצב',
    total: 'סך הכול',
    list: 'הדומיינים של החנויות',
    filter: 'סינון לפי מצב',
    allStatuses: 'כל המצבים',
    empty: 'לאף חנות עוד לא חובר דומיין משלה.',
    emptyFiltered: 'אין דומיינים במצב הזה.',
    hostname: 'כתובת',
    store: 'חנות',
    statusHead: 'מצב',
    reason: 'מה חסר',
    lastChecked: 'בדיקה אחרונה',
    neverChecked: 'טרם נבדקה',
    certificateUntil: 'התעודה בתוקף עד',
    opensInNewTab: 'נפתח בלשונית חדשה',
    status: {
      SYSTEM: 'כתובת המערכת',
      PENDING: 'ממתינה ל-DNS',
      VERIFYING: 'ממתינה לתעודה',
      ACTIVE: 'פעילה',
      FAILED: 'הבדיקה נכשלה',
      DISABLED: 'מושבתת',
      DNS_LOST: 'ה-DNS נותק',
    } satisfies Record<DomainStatus | 'SYSTEM', string>,
    counts: {
      PENDING: 'ממתינות ל-DNS',
      VERIFYING: 'ממתינה לתעודה',
      ACTIVE: 'פעילות',
      FAILED: 'נכשלו',
      DISABLED: 'מושבתות',
      DNS_LOST: 'ה-DNS נותק',
    } satisfies Record<DomainStatus, string>,
    reasons: {
      NO_RECORD: 'אין לכתובת רשומת DNS',
      WRONG_TARGET: 'ה-DNS מפנה למקום אחר',
      OWNERSHIP_NOT_PROVEN: 'מפנה אל המערכת, אבל הבעלות לא הוכחה (חסר CNAME או TXT)',
      CAA_BLOCKED: 'רשומות ה-CAA של הדומיין חוסמות את הנפקת התעודה',
      TLS_ERROR: 'ה-DNS תקין, אבל HTTPS עוד לא עונה בתעודה תקינה',
    } satisfies Record<DomainReason, string>,
    certificate: {
      ENDING: 'מסתיימת בקרוב',
      ENDED: 'פג תוקף',
    },
  },
  storeDomains: {
    title: 'ניהול דומיין',
    back: 'חזרה לחנות',
    manage: 'ניהול דומיין',
    readOnly: 'אין לך הרשאה לשנות את הכתובות של חנות. אפשר לצפות בלבד.',
    forbidden: 'אין לך הרשאה לראות את הכתובות של חנות',
    notFound: 'החנות לא נמצאה. ייתכן שהיא הוסרה; חזרו לרשימת החנויות.',
    actions: 'פעולות',
    statusHead: 'מצב',
    remove: 'הסרה',
    intro:
      'כאן מחברים לחנות דומיין משלה, למשל www.myshop.co.il. כתובת המערכת ממשיכה לעבוד תמיד, ומפנה את הקונים אל הכתובת הראשית. את רשומות ה-DNS מגדיר בעל הדומיין אצל הספק שלו.',
    listTitle: 'הכתובות של החנות',
    hostname: 'כתובת',
    lastChecked: 'בדיקה אחרונה',
    neverChecked: 'טרם נבדקה',
    empty: 'לחנות אין כתובות.',
    system: 'כתובת המערכת',
    primary: 'ראשי',
    makePrimary: 'הפוך לראשי',
    checkNow: 'בדיקה עכשיו',
    issueCertificate: 'הנפקת תעודה',
    issueWaiting:
      'מגדירים את הכתובת בשרת ומנפיקים לה תעודת אבטחה. זה לוקח בדרך כלל עד דקה, ולפעמים כמה דקות. השאירו את הדף פתוח.',
    issueReady:
      'ההפניה אל השרת תקינה והדומיין אומת. נשאר השלב האחרון: לחצו על "הנפקת תעודה", והמערכת תגדיר את הכתובת בשרת ותנפיק לה תעודת אבטחה (HTTPS).',
    issueAgain:
      'הכתובת פעילה, אבל תעודת האבטחה שלה לא תקינה. לחצו על "הנפקת תעודה" כדי להנפיק אותה מחדש.',
    issueResults: {
      ISSUED: 'התעודה הונפקה. הכתובת נבדקת עכשיו, ותהפוך לפעילה תוך רגע.',
      FAILED: 'התעודה לא הונפקה.',
      TIMEOUT:
        'השרת עוד לא סיים. ייתכן שההנפקה עדיין רצה: המתינו כמה דקות ולחצו על "בדיקה עכשיו". אם הכתובת לא הופכת לפעילה, נסו להנפיק שוב.',
    } satisfies Record<CertificateIssueResult, string>,
    issueFailures: {
      NOT_ALLOWED:
        'השרת סירב: הכתובת לא רשומה אצלו ככתובת מאומתת של חנות. לחצו על "בדיקה עכשיו" ונסו שוב.',
      HOST_TAKEN:
        'אתר אחר בשרת כבר עונה לכתובת הזו, ולכן לא נגענו בו. בדקו שזו הכתובת הנכונה; אם כן, מי שמנהל את השרת צריך להסיר קודם את האתר הקיים.',
      WEB_SERVER:
        'שרת האינטרנט דחה את ההגדרה של הכתובת. שום אתר אחר לא השתנה. פנו למי שמנהל את השרת.',
      CERTIFICATE:
        'רשות התעודות לא הנפיקה תעודה. בדרך כלל זה אומר שה-DNS עוד לא התעדכן בכל העולם; המתינו כמה דקות ונסו שוב.',
      UNKNOWN: 'ההנפקה נכשלה מסיבה לא מוכרת. פנו למי שמנהל את השרת.',
    } satisfies Record<CertificateFailure, string>,
    issueOutput: 'מה שהשרת החזיר',
    details: 'פרטים והוראות',
    actionFor: '{action}: {hostname}',
    opensInNewTab: 'נפתח בלשונית חדשה',
    removeTitle: 'להסיר את הכתובת?',
    removeBody:
      'קונים שייכנסו אל {hostname} לא יגיעו יותר לחנות. אפשר להוסיף את הכתובת שוב בכל עת, והיא תיבדק מחדש.',
    addTitle: 'הוספת כתובת',
    addLabel: 'הדומיין של החנות',
    addPlaceholder: 'www.myshop.co.il',
    addHint:
      'שם הדומיין בלבד, בלי https://‎ ובלי נתיב. כל כתובת מוסיפים בנפרד: www.myshop.co.il ו-myshop.co.il הן שתי כתובות.',
    add: 'הוספה',
    taken: 'הכתובת כבר רשומה במערכת',
    full: 'החנות הגיעה למספר הכתובות המרבי ({max}). כדי להוסיף כתובת, הסירו קודם אחת מהקיימות.',
    setupTitle: 'חיבור הכתובת {hostname}',
    detailTitle: 'הכתובת {hostname}',
    setupIntro:
      'כדי שהכתובת תציג את החנות, מוסיפים רשומות DNS אצל החברה שבה רשום הדומיין (למשל במסך "ניהול DNS"). בחרו באחת משתי האפשרויות והעבירו לבעל הדומיין את הערכים כמו שהם. המערכת תזהה את השינוי לבד.',
    setupIntroRoot:
      'כדי שהכתובת תציג את החנות, מוסיפים רשומות DNS אצל החברה שבה רשום הדומיין (למשל במסך "ניהול DNS"). מוסיפים את כל הרשומות שלמטה ומעבירים לבעל הדומיין את הערכים כמו שהם. המערכת תזהה את השינוי לבד.',
    rootOptionTitle: 'רשומות A ו-TXT (לכתובת ראשית)',
    options: {
      CNAME: 'אפשרות 1 (מומלצת) — רשומת CNAME',
      CNAME_TXT: 'אפשרות 1 (מומלצת) — רשומת CNAME ורשומת TXT',
      A_TXT: 'אפשרות 2 — כתובת ראשית (ללא www)',
    } satisfies Record<DnsSetupKind, string>,
    optionText: {
      CNAME:
        'מתאימה לכתובת שמתחילה ב-www (או בכל קידומת אחרת). זו הדרך המועדפת: רשומה אחת, והיא ממשיכה לעבוד גם אם השרת של המערכת יוחלף אי פעם.',
      CNAME_TXT:
        'מתאימה לכתובת שמתחילה ב-www (או בכל קידומת אחרת). רשומת ה-CNAME מפנה את הכתובת אל המערכת, ורשומת ה-TXT מוכיחה שהדומיין שייך לחנות. צריך את שתיהן.',
      A_TXT:
        'נדרשת לכתובת ראשית כמו myshop.co.il, שבה אי אפשר להגדיר CNAME. רשומות הכתובת (A / AAAA) מפנות את הדומיין אל השרת, ורשומת ה-TXT מוכיחה שהדומיין שייך לחנות. שימו לב: אם כתובת השרת של המערכת תשתנה, צריך יהיה לעדכן את רשומת ה-A.',
    } satisfies Record<DnsSetupKind, string>,
    addressesUnknown:
      'אי אפשר להציג כרגע את כתובות השרת (רשומות A). רעננו את הדף בעוד כמה דקות; רשומת ה-TXT שלמטה כבר נכונה.',
    refresh: 'רענון',
    recordsOf: 'הרשומות של {option}',
    recordType: 'סוג',
    recordName: 'שם לרשומה',
    fullName: 'השם המלא: {name}',
    recordValue: 'ערך',
    recordTypes: {
      CNAME: 'CNAME',
      A: 'A',
      AAAA: 'AAAA',
      TXT: 'TXT',
    } satisfies Record<DnsRecordType, string>,
    copy: 'העתקה',
    copyValue: 'העתקת הערך של רשומת {type}: {value}',
    copyName: 'העתקת השם של רשומת {type}: {value}',
    copied: 'הועתק: {value}',
    copyFailed: 'ההעתקה נכשלה. סמנו את הטקסט והעתיקו אותו ידנית.',
    nameGuideTitle: 'מה כותבים בשדה "שם" של כל רשומה',
    nameGuide: [
      'מסך ה-DNS של הדומיין {zone} מוסיף לבד את {zone} לסוף כל שם. לכן בשדה "שם" (שנקרא גם Name או Host) כותבים רק את מה שמופיע בעמודה "שם לרשומה" בטבלה — לא את הכתובת המלאה.',
      '@ פירושו הדומיין {zone} עצמו, בלי קידומת. אם הספק לא מקבל @, משאירים את השדה ריק.',
      'אם אחרי השמירה הרשומה מופיעה כ-{doubled} — הסוף נכתב פעמיים. מוחקים אותה ויוצרים שוב עם מה שבעמודה בלבד.',
      'רק ספק שמבקש במפורש שם מלא מקבל את "השם המלא" שמופיע מתחת לכל רשומה.',
    ],
    twinHint: 'כדי שגם {hostname} תוביל לחנות, הוסיפו אותה ככתובת נוספת. כל כתובת מוסיפים בנפרד.',
    status: {
      PENDING: 'ממתינה ל-DNS',
      VERIFYING: 'ממתינה לתעודה',
      ACTIVE: 'פעילה',
      FAILED: 'הבדיקה נכשלה',
      DISABLED: 'מושבתת',
      DNS_LOST: 'ה-DNS נותק',
    } satisfies Record<DomainStatus, string>,
    statusText: {
      PENDING: 'ממתינים לעדכון ה-DNS. הבדיקה חוזרת אוטומטית; עדכון DNS יכול לקחת עד כמה שעות.',
      VERIFYING:
        'ה-DNS תקין והדומיין אומת. תעודת האבטחה (HTTPS) בהנפקה, וזה לוקח בדרך כלל כמה דקות. הכתובת תתחיל לפעול מעצמה.',
      ACTIVE: 'הכתובת פעילה ומציגה את החנות בחיבור מאובטח (HTTPS).',
      FAILED: 'הבדיקה הופסקה',
      DISABLED: 'הכתובת הושבתה על ידי המערכת.',
      DNS_LOST:
        'הכתובת הפסיקה להפנות אל החנות. בינתיים החנות ממשיכה לפעול בכתובות האחרות שלה. תקנו את רשומות ה-DNS לפי ההוראות למטה, והכתובת תחזור לפעול מעצמה.',
    } satisfies Record<DomainStatus, string>,
    failedHelp: 'תקנו את רשומות ה-DNS לפי ההוראות למטה, ואז לחצו על "נסו שוב".',
    deadline: 'הבדיקות יימשכו עד {date}.',
    tlsValidUntil: 'תעודת האבטחה בתוקף עד {date}, ומתחדשת מעצמה.',
    reasons: {
      NO_RECORD:
        'לא מצאנו רשומת DNS לכתובת הזו. הוסיפו את הרשומות לפי אחת האפשרויות למטה; אם כבר הוספתם, ייתכן שהעדכון עוד לא התפשט.',
      WRONG_TARGET: 'הכתובת מפנה למקום אחר. בדקו שהרשומות זהות לאלה שבטבלאות למטה.',
      OWNERSHIP_NOT_PROVEN:
        'הכתובת מפנה אל המערכת, אבל עוד לא הוכח שהדומיין שייך לחנות. הוסיפו את רשומת ה-TXT שבאפשרות 2, או עברו לרשומת ה-CNAME שבאפשרות 1.',
      CAA_BLOCKED:
        'רשומות ה-CAA של הדומיין לא מאפשרות להנפיק לו תעודת אבטחה. הוסיפו רשומת CAA שמתירה את letsencrypt.org, או הסירו את רשומות ה-CAA.',
      TLS_ERROR: 'ה-DNS תקין, אבל הכתובת לא עונה בחיבור מאובטח (HTTPS) עם תעודה תקינה.',
    } satisfies Record<DomainReason, string>,
    wrongTargetTo: 'הכתובת מפנה למקום אחר. בדקו שהרשומה מפנה אל {target}.',
    observedTitle: 'מה ראינו בבדיקה האחרונה',
    observedCname: 'רשומת CNAME',
    observedNoCname: 'לא נמצאה',
    observedAddresses: 'כתובות IP',
    observedNoAddresses: 'לא נמצאו',
    observedTxt: 'רשומת TXT לאימות',
    observedTxtYes: 'נמצאה ותואמת',
    observedTxtNo: 'לא נמצאה, או שאינה תואמת (נדרשת רק באפשרות 2)',
    observedCaa: 'רשומות CAA',
    observedCaaBlocked: 'חוסמות את הנפקת התעודה',
  },
  storeDesign: {
    title: 'עיצוב החנות',
    back: 'חזרה לחנות',
    manage: 'עריכת עיצוב החנות',
    intro:
      'כאן בוחרים לחנות תבנית, צבעים וגופן. הלוגו, הפביקון ומילות באנר הפתיחה נשארים בידי בעל החנות, והוא יכול לערוך אותם בניהול החנות.',
    readOnly: 'אין לך הרשאה לשנות את עיצוב החנות. אפשר לצפות בלבד.',
    forbidden: 'אין לך הרשאה לראות את עיצוב החנות',
    notFound: 'החנות לא נמצאה. ייתכן שהיא הוסרה; חזרו לרשימת החנויות.',
    view: 'צפייה בחנות',
    noAddress: 'לחנות אין עדיין כתובת פעילה',
    template: 'תבנית',
    templateHint:
      'חמש תבניות, כל אחת חנות שלמה משלה: כותרת עליונה, פתיחה, מחלקות, מוצרים, כותרת תחתונה ודף מוצר, גם בנייד. בחירת תבנית מכניסה את הצבעים והגופן שלה, ואפשר לשנות אותם למטה.',
    templateLookApplied:
      'הוכנסו הצבעים והגופן של התבנית. אפשר לשנות אותם, או לבחור צבעים מהלוגו, לפני השמירה.',
    templates: {
      PANORAMA: {
        name: 'פנורמה',
        description:
          'כחול-פלדה וכתום: פתיחה על כל המסך הראשון עם תמונות המחלקות, ופס המחלקות בתחתיתה.',
      },
      ATELIER: {
        name: 'אטלייה',
        description: 'מגזין: נייר חם, טרקוטה וזית, קווים דקים ואינדקס ממוספר של המחלקות.',
      },
      DEPOT: {
        name: 'מחסן',
        description: 'סיטונאי ונועז: שחור, בטון וצהוב בטיחות, מסגרות עבות וקודי מדף למחלקות.',
      },
      FRESH: {
        name: 'רענן',
        description:
          'ידידותי ומעוגל: מנטה, ירוק וקורל, פתיחה בכרטיסים (בנטו) ועיגולים צבעוניים למחלקות.',
      },
      PLANNER: {
        name: 'מתכנן מטבחים',
        description:
          'בהיר ומכוון לפתרון: לבן, אפור חם ובורדו, בחירת סוג העסק בפתיחה, והמחלקות לפי מסלול העבודה במטבח.',
      },
    } satisfies Record<StoreTemplate, { name: string; description: string }>,
    colors: 'צבעים',
    fromLogo: 'צבעים מהלוגו',
    fromLogoHint: 'הצעות מהצבעים של הלוגו. לחיצה על הצעה מעתיקה אותה לשדות שלמטה.',
    readingLogo: 'קוראים את הצבעים של הלוגו…',
    noLogo: 'לחנות עדיין אין לוגו. בעל החנות מעלה אותו בניהול החנות, תחת "מיתוג".',
    logoFailed: 'לא הצלחנו לקרוא את הלוגו. אפשר לבחור צבעים ידנית.',
    logoColorless: 'בלוגו אין צבעים (רק שחור, לבן או אפור). בחרו צבעים ידנית.',
    palette: 'הצעה {n}',
    paletteChosen: 'נבחרה',
    brand: 'צבע ראשי',
    brandHint: 'כפתורים, קישורים והשורה השנייה של הכותרת.',
    accent: 'צבע משני',
    accentHint: 'סימונים, תגיות והדגשות קטנות.',
    ink: 'צבע כהה',
    inkHint: 'הפס העליון, הכותרת התחתונה ושטחים כהים.',
    hexInvalid: 'כתבו צבע בתבנית #1a2b3c',
    lowContrast:
      'הצבע הראשי בהיר מדי על רקע לבן (ניגודיות {ratio} מתוך 4.5 הנדרשים), וטקסט בצבע הזה יהיה קשה לקריאה.',
    typeface: 'גופן',
    typefaces: {
      INDUSTRIAL: 'תעשייתי – Secular One',
      MODERN: 'מודרני – Rubik',
      CLASSIC: 'קלאסי – Frank Ruhl Libre',
      HEEBO: 'נקי – Heebo',
      ASSISTANT: 'רך – Assistant',
      NOTO: 'ספרותי – Noto Serif / Noto Sans Hebrew',
      SUEZ: 'בולט – Suez One',
      KARANTINA: 'דחוס – Karantina',
    } satisfies Record<StoreTypeface, string>,
    typefaceSample: 'ציוד מקצועי למטבח',
    preview: 'תצוגה מקדימה',
    previewStore: 'שם החנות',
    previewButton: 'לבקשת הצעת מחיר',
    previewCard: 'מוצר לדוגמה',
    previewBadge: 'חדש',
    saved: 'העיצוב נשמר. החנות מציגה אותו מעכשיו.',
    hero: {
      title: 'באנר הפתיחה',
      intro:
        'המילים של הבאנר בראש דף הבית. ה-AI כותב אותן לחנות מהמחלקות שלה, ובעל החנות יכול לערוך אותן בניהול החנות, תחת "דף הבית".',
      eyebrow: 'שורה מעל הכותרת',
      headline: 'כותרת',
      highlight: 'שורה שנייה של הכותרת',
      text: 'טקסט',
      buttonLabel: 'כפתור',
      empty: 'ריק (החנות מציגה את המילים שלה)',
      states: {
        NONE: 'ה-AI עוד לא כתב את הבאנר של החנות.',
        QUEUED: 'הבאנר ממתין לכתיבה…',
        RUNNING: 'ה-AI כותב את הבאנר עכשיו…',
        DONE: 'הבאנר נכתב.',
        FAILED: 'כתיבת הבאנר נכשלה: {reason}',
      } satisfies Record<StoreHeroState, string>,
      finishedAt: 'עודכן לאחרונה',
      rewrite: 'כתיבה מחדש ע״י AI',
      rewriteTitle: 'לכתוב מחדש את באנר הפתיחה?',
      rewriteBody:
        'ה-AI יכתוב את כל מילות הבאנר מחדש, והן יחליפו את המילים שיש עכשיו, גם אם בעל החנות ערך אותן בעצמו.',
      queued: 'הבקשה התקבלה. הבאנר החדש יופיע כאן כשהכתיבה תסתיים.',
      factGuard: 'במילים שנכתבו הופיע מספר או הבטחה שאין להם בסיס במידע של החנות',
      banned: 'במילים שנכתבו הופיע שם שאסור שיופיע בחנות',
    },
  },
  storeContact: {
    title: 'וואטסאפ ומייל',
    back: 'חזרה לחנות',
    manage: 'עריכת וואטסאפ ומייל',
    intro:
      'כאן מגדירים עבור החנות את מספר הוואטסאפ שהקונים כותבים אליו, ואת חשבון ה-Gmail שמקבל את הבקשות להצעת מחיר. גם בעל החנות יכול לשנות אותם בהגדרות החנות שלו.',
    readOnly: 'אין לך הרשאה לשנות את הפרטים האלה. אפשר לצפות בלבד.',
    forbidden: 'אין לך הרשאה לראות את הפרטים האלה',
    notFound: 'החנות לא נמצאה. ייתכן שהיא הוסרה; חזרו לרשימת החנויות.',
    whatsapp: 'מספר וואטסאפ',
    whatsappHint: 'למשל 050-1234567. הקונים כותבים למספר הזה מכפתורי הוואטסאפ בחנות.',
    whatsappFallback: 'כשהשדה ריק, הקונים כותבים לטלפון של החנות:',
    whatsappNone: 'כשהשדה ריק ולחנות אין טלפון, כפתורי הוואטסאפ לא מוצגים בחנות.',
    whatsappSaved: 'מספר הוואטסאפ נשמר',
    whatsappEmpty: 'לא הוגדר',
    whatsappByPhone: 'לא הוגדר. הקונים כותבים לטלפון של החנות:',
    mail: 'Gmail לבקשות',
    mailTitle: 'קבלת בקשות להצעת מחיר במייל',
    mailIntro:
      'כל בקשה חדשה להצעת מחיר נשלחת מיד לחשבון ה-Gmail של החנות. תשובה למייל נשלחת ישירות ללקוח.',
    mailNone: 'לא חובר חשבון Gmail.',
    mailFailedShort: 'Google דחה את הסיסמה, הבקשות לא נשלחות',
    step1: 'מפעילים אימות דו־שלבי בחשבון Google של החנות (חובה כדי ליצור סיסמת אפליקציה).',
    step2: 'יוצרים "סיסמת אפליקציה" בכתובת',
    step3: 'מדביקים כאן את 16 האותיות ש-Google מציג, יחד עם כתובת ה-Gmail.',
    address: 'כתובת ה-Gmail',
    appPassword: 'סיסמת אפליקציה',
    appPasswordHint: '16 אותיות. זו לא הסיסמה הרגילה של החשבון.',
    connect: 'חיבור החשבון',
    connectedTo: 'מחובר. הבקשות נשלחות אל',
    failed:
      'Google דחה את הסיסמה של {address} ({when}), והבקשות לא נשלחות במייל. החליפו את החשבון עם סיסמת אפליקציה חדשה.',
    sendTest: 'שליחת מייל בדיקה',
    testSent: 'מייל הבדיקה נשלח. בדקו את תיבת הדואר של החנות.',
    refused: 'Google דחה את סיסמת האפליקציה. החליפו את החשבון עם סיסמה חדשה.',
    unreachable: 'לא הצלחנו להגיע ל-Gmail כרגע. נסו שוב בעוד רגע.',
    notConnected: 'לא מחובר חשבון Gmail.',
    replace: 'החלפת החשבון',
    replaceIntro: 'החשבון החדש יחליף את הקיים רק אחרי ש-Google יאשר אותו.',
    replaced: 'החשבון הוחלף',
    disconnect: 'ניתוק החשבון',
    disconnectTitle: 'לנתק את חשבון ה-Gmail?',
    disconnectBody: 'בקשות חדשות להצעת מחיר לא יישלחו לחנות במייל. הן ימשיכו להופיע בניהול החנות.',
  },
  service: {
    title: 'תקלות וקריאות שירות',
    everywhereTitle: 'תקלות וקריאות שירות בכל החנויות',
    everywhereIntro:
      'כאן מופיעות כל התקלות והקריאות שעדיין פתוחות בכל החנויות: תקלות שהסריקה האוטומטית מצאה (כל שעה), וקריאות שבעלי החנויות פתחו מניהול החנות. החמורות קודם.',
    everywhereEmpty: 'אין תקלות או קריאות פתוחות באף חנות.',
    truncated: 'מוצגות רק התקלות הראשונות. סגרו חלק מהן כדי לראות את השאר.',
    manage: 'לכל התקלות והקריאות',
    back: 'חזרה לחנות',
    intro:
      'הסריקה האוטומטית עוברת על החנות כל שעה: חשבונות נעולים, ניסיונות כניסה חוזרים, תעודות אבטחה, כתובות, המייל, פרטי הקשר, ההקמה וכתיבת התוכן. תקלה נסגרת מעצמה כשהיא נפתרת, ונפתחת שוב אם היא חוזרת. קריאות שבעל החנות פותח מניהול החנות מופיעות כאן גם הן.',
    notFound: 'החנות לא נמצאה. ייתכן שהיא הוסרה; חזרו לרשימת החנויות.',
    ticketNotFound: 'התקלה או הקריאה לא נמצאה.',
    none: 'אין תקלות פתוחות בחנות.',
    noneAtAll: 'עדיין לא נמצאו בחנות תקלות, ובעל החנות לא פתח קריאות.',
    lastScan: 'סריקה אחרונה',
    neverScanned: 'החנות עוד לא נסרקה',
    scanNow: 'סריקה עכשיו',
    scanned: 'הסריקה הסתיימה',
    openSecurity: 'אבטחה',
    openGeneral: 'תקלות',
    openCalls: 'קריאות מהחנות',
    moreOpen: (count: number) => `ועוד ${String(count)} פתוחות`,
    filterOpen: 'פתוחות',
    filterAll: 'הכל',
    store: 'חנות',
    subject: 'נושא',
    kindLabel: 'סוג',
    severityLabel: 'חומרה',
    statusLabel: 'מצב',
    sourceLabel: 'מקור',
    updated: 'עדכון אחרון',
    opened: 'נפתחה',
    lastSeen: 'נראתה לאחרונה בסריקה',
    resolvedAt: 'נסגרה',
    resolvedByScan: 'נסגרה מעצמה: הסריקה כבר לא מוצאת את הבעיה',
    messages: (count: number) => (count === 1 ? 'הודעה אחת' : `${String(count)} הודעות`),
    pick: 'בחרו תקלה או קריאה מהרשימה כדי לראות את הפרטים ולענות.',
    fix: 'למסך הטיפול',
    setStatus: 'שינוי מצב',
    statusSaved: 'המצב עודכן',
    scanReopens: 'תקלה מהסריקה שתסומן כ"טופלה" בזמן שהבעיה עדיין קיימת תיפתח שוב בסריקה הבאה.',
    readOnly: 'אין לך הרשאה לענות או לשנות מצב. אפשר לצפות בלבד.',
    kinds: { SECURITY: 'אבטחה', GENERAL: 'תקלה כללית' } satisfies Record<ServiceTicketKind, string>,
    sources: {
      SCAN: 'סריקה אוטומטית',
      STORE: 'פנייה של החנות',
    } satisfies Record<ServiceTicketSource, string>,
    severities: { HIGH: 'גבוהה', MEDIUM: 'בינונית', LOW: 'נמוכה' } satisfies Record<
      ServiceTicketSeverity,
      string
    >,
    statuses: {
      OPEN: 'פתוחה',
      IN_PROGRESS: 'בטיפול',
      RESOLVED: 'טופלה',
    } satisfies Record<ServiceTicketStatus, string>,
    checks: {
      ACCOUNT_LOCKED: 'חשבון של החנות ננעל אחרי סיסמאות שגויות',
      FAILED_SIGN_INS: 'ניסיונות כניסה חוזרים עם סיסמה שגויה',
      CERTIFICATE_EXPIRING: 'תעודת האבטחה (HTTPS) עומדת לפוג',
      OWNER_WITHOUT_PASSWORD: 'בעל החנות עוד לא קבע סיסמה',
      DOMAIN_PROBLEM: 'כתובת של החנות לא עובדת',
      MAIL_FAILED: 'המייל של החנות לא שולח את הבקשות',
      NO_CONTACT: 'אין לחנות טלפון או וואטסאפ',
      SETUP_FAILED: 'הקמת החנות נכשלה',
      COPY_FAILED: 'כתיבת התוכן השיווקי נכשלה',
    } satisfies Record<ServiceCheck, string>,
    explain: {
      ACCOUNT_LOCKED: (count: number) =>
        `${count === 1 ? 'חשבון אחד' : `${String(count)} חשבונות`} של החנות נעולים כרגע אחרי כמה סיסמאות שגויות ברצף. ייתכן שמישהו מנסה לנחש סיסמה. החשבון נפתח מעצמו אחרי זמן קצר.`,
      FAILED_SIGN_INS: (count: number, attempts: number) =>
        `${count === 1 ? 'לחשבון אחד' : `ל-${String(count)} חשבונות`} של החנות יש עד ${String(attempts)} סיסמאות שגויות ברצף. אם זה לא בעל החנות, ייתכן שמישהו מנסה לנחש סיסמה.`,
      CERTIFICATE_EXPIRING: (hostname: string, until: string) =>
        `התעודה של ${hostname} בתוקף עד ${until}. בלי תעודה בתוקף הדפדפן יזהיר את הקונים.`,
      OWNER_WITHOUT_PASSWORD:
        'בעל החנות עוד לא קבע סיסמה ולא נכנס לניהול החנות. אפשר לשלוח לו הזמנה חדשה או לקבוע לו סיסמה בכרטיס "בעלי החנות".',
      DOMAIN_PROBLEM: (hostname: string, status: string) => `${hostname}: ${status}`,
      MAIL_FAILED: (since: string) =>
        `מאז ${since} Google דוחה את חשבון ה-Gmail של החנות, ולכן בקשות להצעת מחיר לא מגיעות במייל.`,
      NO_CONTACT: 'לחנות אין מספר טלפון ואין מספר וואטסאפ, ולכן כפתורי הפנייה לא מוצגים לקונים.',
      SETUP_FAILED: (step: string) => `ההקמה נעצרה בשלב: ${step}.`,
      COPY_FAILED: (count: number) =>
        `התוכן השיווקי של ${count === 1 ? 'מוצר אחד' : `${String(count)} מוצרים`} לא נכתב בשבוע האחרון.`,
    },
    reply: {
      platform: 'צוות הפלטפורמה',
      store: 'החנות',
      empty: 'אין עדיין הודעות.',
      reply: 'הודעה לחנות',
      replyScan: 'הערה פנימית (החנות לא רואה תקלות מהסריקה)',
      send: 'שליחה',
      sent: 'ההודעה נשלחה',
    },
  },
  roles: {
    PLATFORM_OWNER: 'בעלים',
    PLATFORM_OPERATOR: 'מפעיל',
    PLATFORM_SUPPORT: 'תמיכה',
  } satisfies Record<PlatformRole, string>,
  tenantStatus: {
    PROVISIONING: 'בהקמה',
    ACTIVE: 'פעילה',
    SUSPENDED: 'מושעית',
    CANCELLED: 'בוטלה',
    CLOSED: 'נסגרה',
  } satisfies Record<TenantStatus, string>,
  tenantKind: {
    MASTER: 'חנות האם',
    CUSTOMER: 'לקוח',
  } satisfies Record<TenantKind, string>,
  actions: {
    'tenant.created': 'חנות נוצרה',
    'tenant.suspended': 'חנות הושעתה',
    'tenant.reactivated': 'חנות הופעלה מחדש',
    'tenant.activated': 'ניהול החנות נפתח לבעלים (הקמה)',
    'tenant.management_entered': 'איש צוות הפלטפורמה נכנס לניהול החנות',
    'tenant.customer_number_changed': 'החנות שויכה ללקוח של האתר המקורי',
    'tenant.customer_refreshed': 'פרטי הלקוח והמחירים נקראו שוב מהאתר המקורי',
    'provisioning.started': 'הקמת החנות התחילה',
    'provisioning.retry_requested': 'הקמת החנות נשלחה לניסיון חוזר',
    'provisioning.failed': 'הקמת החנות נכשלה',
    'store.opened': 'החנות נפתחה לקונים',
    'store.updated': 'פרטי חנות עודכנו',
    'auth.login_succeeded': 'כניסה',
    'auth.login_failed': 'כניסה נכשלה',
    'auth.account_locked': 'חשבון ננעל',
    'auth.logout': 'יציאה',
    'auth.tenant_switched': 'מעבר חנות',
    'auth.password_changed': 'סיסמה שונתה',
    'auth.password_reset_requested': 'נשלח קישור לאיפוס סיסמה',
    'auth.password_reset': 'סיסמה אופסה דרך קישור במייל',
    'auth.password_set_by_platform': 'צוות הפלטפורמה קבע סיסמה חדשה',
    'auth.mfa_enrolled': 'אימות דו־שלבי הוגדר',
    'auth.mfa_verified': 'אימות דו־שלבי עבר',
    'auth.mfa_failed': 'קוד אימות שגוי',
    'auth.mfa_recovery_code_used': 'שימוש בקוד שחזור',
    'auth.mfa_recovery_codes_regenerated': 'קודי שחזור חודשו',
    'auth.mfa_reset': 'אימות דו־שלבי אופס (שרת)',
    'session.revoked': 'התחברות נותקה',
    'membership.role_granted': 'הרשאה ניתנה',
    'membership.role_changed': 'הרשאה שונתה',
    'membership.revoked': 'הרשאה הוסרה',
    'platform.role_granted': 'צורף לצוות הפלטפורמה',
    'platform.role_changed': 'תפקיד בפלטפורמה שונה',
    'platform.role_revoked': 'הוסר מצוות הפלטפורמה',
    'invitation.issued': 'קישור הזמנה נוצר',
    'invitation.accepted': 'הזמנה מומשה',
    'catalog.ingest_requested': 'התבקש עדכון קטלוג',
    'catalog_sync.requested': 'התבקשה משיכת מוצרים חדשים',
    'catalog_sync.completed': 'משיכת המוצרים הושלמה',
    'catalog_sync.failed': 'משיכת המוצרים נכשלה',
    'product.created': 'מוצר נוסף',
    'product.updated': 'מוצר עודכן',
    'category.created': 'קטגוריה נוספה',
    'category.updated': 'קטגוריה עודכנה',
    'category.deleted': 'קטגוריה נמחקה',
    'auth.mfa_disabled': 'אימות דו־שלבי בוטל',
    'product.media_changed': 'תמונות מוצר עודכנו',
    'product.logo_changed': 'לוגו יצרן של מוצר עודכן',
    'product.list_restored': 'רשימה של מוצר הוחזרה לכתיבה אוטומטית',
    'media.uploaded': 'תמונה הועלתה',
    'settings.updated': 'הגדרות החנות עודכנו',
    'theme.updated': 'המיתוג או העיצוב של החנות עודכנו',
    'home.updated': 'דף הבית עודכן',
    'home_slide.created': 'באנר נוסף לדף הבית',
    'home_slide.updated': 'באנר בדף הבית עודכן',
    'home_slide.deleted': 'באנר נמחק מדף הבית',
    'content_page.created': 'עמוד תוכן נוסף',
    'content_page.updated': 'עמוד תוכן עודכן',
    'content_page.deleted': 'עמוד תוכן נמחק',
    'shipping_zone.created': 'אזור משלוח נוסף',
    'shipping_zone.updated': 'אזור משלוח עודכן',
    'shipping_zone.deleted': 'אזור משלוח נמחק',
    'coupon.created': 'קופון נוסף',
    'coupon.updated': 'קופון עודכן',
    'coupon.deleted': 'קופון נמחק',
    'promotion.created': 'מבצע כמות נוסף',
    'promotion.updated': 'מבצע כמות עודכן',
    'promotion.deleted': 'מבצע כמות נמחק',
    'popup.created': 'חלון קופץ נוסף',
    'popup.updated': 'חלון קופץ עודכן',
    'popup.deleted': 'חלון קופץ נמחק',
    'order.status_changed': 'סטטוס של בקשה להצעת מחיר שונה',
    'order.quote_saved': 'הצעת מחיר נשמרה',
    'order.payment_changed': 'מצב תשלום שונה (היסטורי)',
    'customer.status_changed': 'מצב חשבון לקוח שונה',
    'ai_copy.approved': 'תוכן שיווקי אושר',
    'ai_copy.regeneration_requested': 'התבקשה כתיבה מחדש של תוכן שיווקי',
    'ai_copy.retry_requested': 'התבקש ניסיון חוזר לכתיבת תוכן שיווקי',
    'ai_copy.generation_requested': 'התבקשה כתיבת תוכן שיווקי',
    'ai_copy.flyers_requested': 'התבקשה כתיבת פליירים שיווקיים',
    'ai_copy.settings_updated': 'הגדרות התוכן השיווקי עודכנו',
    'ai_copy.group_edited': 'התוכן השיווקי של קבוצת מוצרים נערך',
    'ai_budget.updated': 'מכסת התוכן החודשית של חנות עודכנה',
    'product_exclusions.updated': 'המוצרים המוצגים בחנות עודכנו',
    'catalog_exposure.updated': 'הנתונים המוצגים בחנות מעבר לאתר המקורי עודכנו',
    'domain.added': 'נוספה כתובת לחנות',
    'domain.removed': 'כתובת הוסרה מהחנות',
    'domain.check_requested': 'התבקשה בדיקה של כתובת',
    'domain.certificate_requested': 'התבקשה הנפקת תעודת אבטחה לכתובת',
    'domain.primary_changed': 'הכתובת הראשית של החנות הוחלפה',
    'domain.ownership_verified': 'הבעלות על כתובת אומתה',
    'domain.activated': 'כתובת התחילה לפעול',
    'domain.failed': 'בדיקת כתובת נכשלה',
    'domain.dns_lost': 'כתובת הפסיקה להפנות אל החנות',
    'domain.restored': 'כתובת חזרה לפעול',
    'service_ticket.opened': 'נפתחה תקלה או קריאת שירות',
    'service_ticket.replied': 'נכתבה הודעה בקריאת שירות',
    'service_ticket.status_changed': 'מצב של תקלה או קריאה השתנה',
    'service_ticket.scan_requested': 'סריקת תקלות הופעלה ידנית',
  } satisfies Record<AuditAction, string>,
  errors: {
    VALIDATION_FAILED: 'יש שדות שאינם תקינים',
    BAD_REQUEST: 'הבקשה אינה תקינה',
    UNAUTHENTICATED: 'נדרשת כניסה מחדש',
    SESSION_EXPIRED: 'פג תוקף ההתחברות. היכנסו שוב.',
    MFA_REQUIRED: 'נדרש קוד אימות',
    FORBIDDEN: 'אין הרשאה לפעולה הזו',
    MFA_ENROLLMENT_REQUIRED: 'יש להגדיר אימות דו־שלבי',
    TENANT_SUSPENDED: 'החנות מושעית',
    NOT_FOUND: 'לא נמצא',
    METHOD_NOT_ALLOWED: 'הפעולה אינה נתמכת',
    CONFLICT: 'הפעולה מתנגשת במצב הנוכחי',
    PAYLOAD_TOO_LARGE: 'הבקשה גדולה מדי',
    UNSUPPORTED_MEDIA_TYPE: 'סוג תוכן לא נתמך',
    RATE_LIMITED: 'יותר מדי ניסיונות. המתינו מעט ונסו שוב.',
    INTERNAL: 'אירעה תקלה בשרת. נסו שוב מאוחר יותר.',
    SERVICE_UNAVAILABLE: 'השרת אינו זמין כרגע. בדקו את החיבור ונסו שוב.',
    STORE_NOT_OPEN: 'החנות עדיין לא פתוחה לקונים',
    STORE_CLOSED: 'החנות סגורה זמנית',
    ORDERING_CLOSED: 'החנות אינה מקבלת הזמנות כרגע',
    CART_INVALID: 'חלק מהמוצרים בסל השתנו',
  } satisfies Record<ErrorCode, string>,
  serverMessages,
  fields,
  validation,
};
